import React, { useContext } from 'react';
import { Tab } from 'react-bootstrap';
import { SearchResultsTab } from '../../../../types/search';
import { useNewDesign } from '../../../../state/DesignPreferenceContextProvider';
import InfiniteScroll from 'react-infinite-scroller';
import { hasNext } from '../../../../utils/pagination';
import WithTracking from '../../../utils/WithTracking';
import classNames from 'classnames';
import EmptySearchResultsNewUI from '../EmptySearchResultsNewUI';
import { trackingEvents } from '../../../../services/tracking/trackConfig';
import SearchResultsContainer from '../SearchResultsContainer';
import EmptySearchResults from '../EmptySearchResults';
import { useSelector } from 'react-redux';
import {
  getUnifiedSearchResults as getUnifiedSearchResultsSelector,
  getUnifiedLoadingState as getUnifiedLoadingStateSelector,
  SearchLoadingStatus,
  getUnifiedPaginationSelector,
} from '../../../../reducers/search.selectors';
import { useViewMode } from '../../../ViewSwitch/ViewModeContext';
import { Link } from 'found';
import { routes } from '../../../../constants';
import SingleSearchResultContainer from '../SingleSearchResultContainer';
import VideoMuiCard from '../../../cards/MuiCard/VideoMuiCard/VideoMuiCard';
import { MindItemSearchResult } from '@klab-berlin/api-sdk/lib/types/responses/MindItem';
import SearchResultsLoader from '../SearchResultsLoader';
import MindItemListItem from '../../../cards/MindItemListItem';
import { selectUser } from '../../../../reducers/user.selectors';
import { getVideoMuiCardProps } from './VideoMindItemsTab';
import MindItemMuiCard from '../../../cards/MuiCard/MindItemMuiCard/MindItemMuiCard';
import { getProductMuiCardProps } from './ProductsTab';
import ProductMuiCard from '../../../cards/MuiCard/ProductMuiCard/ProductMuiCard';
import { getMindItemMuiCardProps } from './MindItemsTab';
import MindItemCard from '../../../cards/MindItemCard';
import { SearchResultsTabProps } from './SearchResultsTab';
import SearchResultsTopbar from '../SearchResultsTopbar';
import { MasterLayoutContext } from '../../../../state/MasterLayoutProvider';
import { useNewNavigation } from '../../../../utils/useNewNavigation';

const UnifiedItemsTab = ({
  searchTerm,
  requestFilters,
  loadMore,
  searchEngineVersion,
  userActionConfig,
  currentTab,
}: SearchResultsTabProps) => {
  const { viewMode } = useViewMode();
  const { prefersNewDesign } = useNewDesign();
  const user = useSelector(selectUser);

  const unified = useSelector((state) => getUnifiedSearchResultsSelector(state));
  const unifiedLoadingState = useSelector(getUnifiedLoadingStateSelector);
  const unifiedPagination = useSelector(getUnifiedPaginationSelector);
  const { mainContentContainer } = useContext(MasterLayoutContext);
  const showNewNavigation = useNewNavigation();

  return user?.isCoreMember && currentTab === 'all' ? (
    <Tab.Pane eventKey={SearchResultsTab.ALL} data-testid='new-ui-all-tab'>
      <SearchResultsTopbar
        total={unifiedPagination.total}
        searchTerm={searchTerm}
        isLoaded={unifiedLoadingState === SearchLoadingStatus.LOADED}
        requestFilters={requestFilters}
        loadedCount={unified.length} //old UI prop
      />
      {unifiedLoadingState === SearchLoadingStatus.INITIAL ? (
        <SearchResultsLoader />
      ) : (
        <>
          {unified.length > 0 ? (
            <InfiniteScroll
              loadMore={loadMore}
              hasMore={hasNext(unifiedPagination)}
              className='search-results__infinite-scroll-box'
              {...(showNewNavigation ? { useWindow: false, getScrollParent: () => mainContentContainer } : {})}
            >
              {prefersNewDesign ? (
                <SearchResultsContainer>
                  {unified.map((item: any, index) => (
                    <SingleSearchResultContainer key={item.id}>
                      {item.fileType === 'video' ? (
                        <WithTracking
                          events={{
                            onClick: trackingEvents.itemOpen,
                            onMiddleClick: trackingEvents.itemOpen,
                            onContextMenu: trackingEvents.itemOpen,
                          }}
                          eventPayload={{
                            other: {
                              minditem_id: item.id,
                              index,
                              searchEngineVersion,
                            },
                          }}
                        >
                          <div>
                            <VideoMuiCard
                              {...getVideoMuiCardProps(item)}
                              index={index}
                            />
                          </div>
                        </WithTracking>
                      ) : item.fileType === 'document' ? (
                        <WithTracking
                          events={{
                            onClick: trackingEvents.itemOpen,
                            onMiddleClick: trackingEvents.itemOpen,
                            onContextMenu: trackingEvents.itemOpen,
                          }}
                          eventPayload={{
                            other: {
                              minditem_id: item.id,
                              index,
                              searchEngineVersion,
                            },
                          }}
                        >
                          <div>
                            <MindItemMuiCard
                              {...getMindItemMuiCardProps(item)}
                              index={index}
                            />
                          </div>
                        </WithTracking>
                      ) : (
                        <WithTracking
                          events={{
                            onClick: trackingEvents.itemOpen,
                            onMiddleClick: trackingEvents.itemOpen,
                            onContextMenu: trackingEvents.itemOpen,
                          }}
                          eventPayload={{
                            other: {
                              product_id: item.id,
                              index,
                              searchEngineVersion,
                            },
                          }}
                        >
                          <div>
                            <ProductMuiCard
                              {...getProductMuiCardProps(item)}
                              index={index}
                            />
                          </div>
                        </WithTracking>
                      )}
                    </SingleSearchResultContainer>
                  ))}
                </SearchResultsContainer>
              ) : (
                <div
                  className={classNames({
                    'search-results__item-grid': viewMode === 'grid',
                    'search-results__item-list': viewMode === 'list',
                  })}
                >
                  {unified.map((item, index) => (
                    <Link
                      key={item.id}
                      to={
                        item.type !== 'product'
                          ? `/${routes.reader.root}/${item.id}`
                          : `/${routes.productPage.root}/${item.id}`
                      }
                      className='search-results__link'
                    >
                      {viewMode === 'grid' ? (
                        <MindItemCard
                          {...(item as MindItemSearchResult)}
                          {...userActionConfig}
                          index={index}
                          searchEngineVersion={searchEngineVersion}
                        />
                      ) : (
                        <MindItemListItem
                          {...(item as MindItemSearchResult)}
                          {...userActionConfig}
                          index={index}
                          searchEngineVersion={searchEngineVersion}
                        />
                      )}
                    </Link>
                  ))}
                </div>
              )}
            </InfiniteScroll>
          ) : prefersNewDesign ? (
            <SearchResultsContainer>
              <EmptySearchResultsNewUI searchTerm={searchTerm} />
            </SearchResultsContainer>
          ) : (
            <EmptySearchResults />
          )}
          {unifiedLoadingState === SearchLoadingStatus.PAGINATION && (
            <SearchResultsLoader />
          )}
        </>
      )}
    </Tab.Pane>
  ) : null;
};

export default UnifiedItemsTab;
